import { create } from 'zustand';

import { IRole } from '../models/role';

export type TLPRolesSortBy = 'name' | 'createdAt' | 'updatedAt';

interface IRolesStore {
  rowData: IRole[];
  pageSize: number;
  offset: number;
  sortBy: TLPRolesSortBy;
  order: 'ASC' | 'DESC';
  defaultSort: {
    sortBy: TLPRolesSortBy;
    order: 'ASC' | 'DESC';
  };
  error: string | boolean;
  loading: boolean;
  count: number;
  dispatch: (args: TLPRolesReducerInput) => void;
  total: number;
}

type TLPRolesReducerInput =
  | {
      type: 'updateRowData';
      args: { rowData: any[] };
    }
  | {
      type: 'updateError';
      args: { error: string | boolean };
    }
  | {
      type: 'updateLoading';
      args: { loading: boolean };
    }
  | {
      type: 'updatePagination';
      args: { pageSize: number; offset: number };
    }
  | {
      type: 'updateSorting';
      args: {
        sortBy: TLPRolesSortBy;
        order: 'ASC' | 'DESC';
      };
    }
  | {
      type: 'updateCount';
      args: { count: number };
    }
  | {
      type: 'updateTotal';
      args: { total: number };
    }
  | {
      type: 'updateDefaultSort';
    }
  | {
      type: 'reset';
    };

const lpRolesReducer = (state: IRolesStore, input: TLPRolesReducerInput) => {
  switch (input.type) {
    case 'updateRowData':
      return { ...state, rowData: input.args.rowData };
    case 'updateError':
      return { ...state, error: input.args.error };
    case 'updateLoading':
      return { ...state, loading: input.args.loading };
    case 'updatePagination':
      return {
        ...state,
        pageSize: input.args.pageSize,
        offset: input.args.offset,
      };
    case 'updateSorting':
      return { ...state, sortBy: input.args.sortBy, order: input.args.order };
    case 'updateCount':
      return { ...state, count: input.args.count };
    case 'updateTotal':
      return { ...state, total: input.args.total };
    case 'updateDefaultSort':
      return {
        ...state,
        defaultSort: { sortBy: state.sortBy, order: state.order },
      };
    case 'reset':
      return INITIAL_STATE;
  }
};

const INITIAL_STATE: Omit<IRolesStore, 'dispatch'> = {
  rowData: [],
  error: '',
  loading: false,
  pageSize: 25,
  offset: 0,
  sortBy: 'updatedAt',
  order: 'DESC',
  defaultSort: {
    sortBy: 'updatedAt',
    order: 'DESC',
  },
  count: 0,
  total: 0,
};

const useRolesStore = create<IRolesStore>((set) => ({
  ...INITIAL_STATE,
  dispatch: (input) => set((state) => lpRolesReducer(state, input)),
}));

export default useRolesStore;
